import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Common countries for address
const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'UAE', 'Saudi Arabia', 'Singapore', 'Malaysia', 'Germany', 'France',
  'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Japan', 'China',
  'South Korea', 'Thailand', 'Indonesia', 'Philippines', 'Vietnam',
  'Brazil', 'Mexico', 'South Africa', 'Nigeria', 'Kenya', 'Egypt',
  'New Zealand', 'Ireland', 'Qatar', 'Bahrain', 'Oman', 'Kuwait'
].sort();

// Indian states
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
].sort();

// US states
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma',
  'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
].sort();

// Get states based on country
const getStatesForCountry = (country) => {
  switch (country) {
    case 'India':
      return INDIAN_STATES;
    case 'United States':
      return US_STATES;
    default:
      return [];
  }
};

export default function AddressInput({
  value,
  onChange,
  disabled = false,
  required = false,
  showCountry = true,
  className = '',
  'data-testid': dataTestId = 'address-input'
}) {
  // Handle null, undefined, empty string, or valid object
  const safeValue = value && typeof value === 'object' ? value : {};
  
  const address = {
    address_line1: safeValue.address_line1 || '',
    address_line2: safeValue.address_line2 || '',
    city: safeValue.city || '',
    state: safeValue.state || '',
    pin_code: safeValue.pin_code || '',
    country: safeValue.country || 'India'
  };

  const handleChange = (field, newValue) => {
    const updated = { ...address, [field]: newValue };
    
    // Reset state if country changes
    if (field === 'country' && newValue !== address.country) {
      updated.state = '';
    }
    
    if (onChange) {
      onChange(updated);
    }
  };

  const states = getStatesForCountry(address.country);
  const pinLabel = address.country === 'United States' ? 'ZIP Code' : 'PIN Code';

  return (
    <div className={`space-y-4 ${className}`} data-testid={dataTestId}>
      {/* Address Line 1 */}
      <div className="space-y-1.5">
        <Label htmlFor={`${dataTestId}-line1`}>
          Address Line 1 {required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id={`${dataTestId}-line1`}
          value={address.address_line1}
          onChange={(e) => handleChange('address_line1', e.target.value)}
          placeholder="Street address, building name"
          disabled={disabled}
          required={required}
          data-testid={`${dataTestId}-line1`}
        />
      </div>

      {/* Address Line 2 */}
      <div className="space-y-1.5">
        <Label htmlFor={`${dataTestId}-line2`}>Address Line 2</Label>
        <Input
          id={`${dataTestId}-line2`}
          value={address.address_line2}
          onChange={(e) => handleChange('address_line2', e.target.value)}
          placeholder="Apartment, suite, floor (optional)"
          disabled={disabled}
          data-testid={`${dataTestId}-line2`}
        />
      </div>

      {/* City and State Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${dataTestId}-city`}>
            City {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={`${dataTestId}-city`}
            value={address.city}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
            disabled={disabled}
            required={required}
            data-testid={`${dataTestId}-city`}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${dataTestId}-state`}>
            State {required && <span className="text-red-500">*</span>}
          </Label>
          {states.length > 0 ? (
            <Select
              value={address.state}
              onValueChange={(val) => handleChange('state', val)}
              disabled={disabled}
            >
              <SelectTrigger data-testid={`${dataTestId}-state`}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={`${dataTestId}-state`}
              value={address.state}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder="State/Province"
              disabled={disabled}
              required={required}
              data-testid={`${dataTestId}-state`}
            />
          )}
        </div>
      </div>

      {/* PIN/ZIP and Country Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${dataTestId}-pincode`}>
            {pinLabel} {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={`${dataTestId}-pincode`}
            value={address.pin_code}
            onChange={(e) => handleChange('pin_code', e.target.value.replace(/[^0-9A-Za-z\s-]/g, ''))}
            placeholder={address.country === 'United States' ? '12345' : '500001'}
            disabled={disabled}
            required={required}
            maxLength={10}
            data-testid={`${dataTestId}-pincode`}
          />
        </div>

        {showCountry && (
          <div className="space-y-1.5">
            <Label htmlFor={`${dataTestId}-country`}>
              Country {required && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={address.country}
              onValueChange={(val) => handleChange('country', val)}
              disabled={disabled}
            >
              <SelectTrigger data-testid={`${dataTestId}-country`}>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper to format address as single string
export function formatAddress(address) {
  if (!address) return '';
  
  const parts = [
    address.address_line1,
    address.address_line2,
    address.city,
    address.state,
    address.pin_code,
    address.country
  ].filter(Boolean);
  
  return parts.join(', ');
}

// Helper to format address as multi-line
export function formatAddressMultiLine(address) {
  if (!address) return '';
  
  const lines = [];
  if (address.address_line1) lines.push(address.address_line1);
  if (address.address_line2) lines.push(address.address_line2);
  
  const cityStateZip = [address.city, address.state, address.pin_code].filter(Boolean).join(', ');
  if (cityStateZip) lines.push(cityStateZip);
  
  if (address.country) lines.push(address.country);
  
  return lines.join('\n');
}

// Simple display component
export function AddressDisplay({ address, className = '', multiLine = false }) {
  if (!address || (!address.address_line1 && !address.city)) {
    return <span className="text-muted-foreground">-</span>;
  }
  
  if (multiLine) {
    return (
      <div className={className}>
        {address.address_line1 && <div>{address.address_line1}</div>}
        {address.address_line2 && <div>{address.address_line2}</div>}
        <div>
          {[address.city, address.state, address.pin_code].filter(Boolean).join(', ')}
        </div>
        {address.country && <div>{address.country}</div>}
      </div>
    );
  }
  
  return <span className={className}>{formatAddress(address)}</span>;
}

export { COUNTRIES, INDIAN_STATES, US_STATES, getStatesForCountry };
